/*
 * Throwaway preview — proposed tab consolidation for Orders page +
 * seller Dashboard "Pulse" cards.
 *
 * Two portals shown side-by-side, plus a Before/After toggle so the
 * user can see exactly what changes:
 *
 *   Buyer:  8 tabs → 6 tabs (Journey · To pay · Schedule pickup ·
 *           In progress · Delivered · Claims)
 *   Seller: 8 tabs → 5 tabs (Action needed · In progress · Delivered
 *           · Claims · All) + Dashboard Pulse cards
 *
 * Hit /preview/orders-tabs-redesign to view.
 */
import { useState } from "react";
import {
  Sparkles, FileText, Clock, CreditCard, Package, Truck, Check, AlertTriangle,
  Zap, Activity, ChevronRight, ArrowRight, Pencil, Calendar, DollarSign,
  TrendingUp, Box, Bell, Users, Layers,
} from "lucide-react";

const PORTALS = [
  { key: "buyer",  label: "Buyer view"  },
  { key: "seller", label: "Seller view" },
];

const MODES = [
  { key: "after",  label: "Proposed" },
  { key: "before", label: "Today"    },
];

// ── Tab definitions ─────────────────────────────────────────────────────
const BUYER_BEFORE = [
  { key: "journey",  label: "Journey",    icon: Sparkles,       count: null,   tone: "" },
  { key: "all",      label: "All",        icon: FileText,       count: 47 },
  { key: "confirm",  label: "Confirming", icon: Clock,          count: 3,      tone: "amber" },
  { key: "pay",      label: "To pay",     icon: CreditCard,     count: 2,      tone: "rose", action: true },
  { key: "pack",     label: "Preparing",  icon: Package,        count: 8,      tone: "blue" },
  { key: "transit",  label: "Shipping",   icon: Truck,          count: 12,     tone: "indigo" },
  { key: "done",     label: "Delivered",  icon: Check,          count: 19,     tone: "emerald" },
  { key: "claims",   label: "Claims",     icon: AlertTriangle,  count: 1,      tone: "rose" },
];
const BUYER_AFTER = [
  { key: "journey",  label: "Journey",        icon: Sparkles,      count: null },
  { key: "all",      label: "All",            icon: FileText,      count: 47 },
  { key: "pay",      label: "To pay",         icon: CreditCard,    count: 2,  tone: "rose",  action: true },
  { key: "pickup",   label: "Schedule pickup",icon: Truck,         count: 4,  tone: "amber", action: true, isNew: true },
  { key: "progress", label: "In progress",    icon: Activity,      count: 19, tone: "blue" },
  { key: "done",     label: "Delivered",      icon: Check,         count: 19, tone: "emerald" },
  { key: "claims",   label: "Claims",         icon: AlertTriangle, count: 1,  tone: "rose" },
];

const SELLER_BEFORE = [
  { key: "all",         label: "All",          icon: FileText,    count: 285 },
  { key: "confirm",     label: "Confirming",   icon: Clock,       count: 14,  tone: "amber",  action: true },
  { key: "awaiting",    label: "Awaiting pay", icon: CreditCard,  count: 22,  tone: "rose" },
  { key: "preparing",   label: "Preparing",    icon: Package,     count: 41,  tone: "blue",   action: true },
  { key: "ready",       label: "Ready",        icon: Truck,       count: 23,  tone: "teal" },
  { key: "transit",     label: "In transit",   icon: Truck,       count: 56,  tone: "indigo" },
  { key: "delivered",   label: "Delivered",    icon: Check,       count: 121, tone: "emerald" },
  { key: "claims",      label: "Claims",       icon: AlertTriangle, count: 8, tone: "rose" },
];
const SELLER_AFTER = [
  { key: "all",      label: "All",           icon: FileText,      count: 285 },
  { key: "action",   label: "Action needed", icon: Zap,           count: 55,  tone: "amber",  action: true },
  { key: "progress", label: "In progress",   icon: Activity,      count: 101, tone: "blue" },
  { key: "delivered",label: "Delivered",     icon: Check,         count: 121, tone: "emerald" },
  { key: "claims",   label: "Claims",        icon: AlertTriangle, count: 8,   tone: "rose" },
];

// ── Mock rows that show how merged tabs differentiate sub-states ────────
const SELLER_ACTION_ROWS = [
  { code: "ORD-9827", buyer: "Apex Beauty",     stage: "confirm",   chip: "amber",  emoji: "⏳", label: "Awaiting confirm",      total: "$1,840.00", age: "2h" },
  { code: "ORD-7866", buyer: "Wholesale Hut",   stage: "confirm",   chip: "amber",  emoji: "⏳", label: "Awaiting confirm",      total: "$420.00",   age: "8h", sla: true },
  { code: "ORD-2612", buyer: "Glow Studio",     stage: "preparing", chip: "blue",   emoji: "📦", label: "Pack & enter shipping",total: "$2,180.00", age: "1d" },
  { code: "ORD-3552", buyer: "Apex Beauty",     stage: "preparing", chip: "blue",   emoji: "📦", label: "Pack & enter shipping",total: "$320.00",   age: "3h" },
];
const SELLER_PROGRESS_ROWS = [
  { code: "ORD-7042", buyer: "Apex Beauty",   stage: "awaiting", chip: "rose",   emoji: "💳", label: "Awaiting payment",       total: "$880.00",  age: "5d", warn: "overdue 2d" },
  { code: "ORD-1653", buyer: "Glow Studio",   stage: "ready",    chip: "teal",   emoji: "📦", label: "Buyer to book pickup",   total: "$1,200.00",age: "3d", warn: "buyer idle 3d" },
  { code: "ORD-4451", buyer: "Wholesale Hut", stage: "transit",  chip: "indigo", emoji: "🚚", label: "In transit · ETA tomorrow",total: "$640.00",age: "1d" },
];
const BUYER_PAY_ROWS = [
  { code: "ORD-3119", seller: "GlowCo Cosmetics", invoice: "INV-1042", total: "$480.00", due: "Due in 2d", chip: "rose", emoji: "💳" },
  { code: "ORD-3220", seller: "Sunshine Beauty",  invoice: "INV-2210", total: "$1,260.00",due: "Due today",chip: "rose", emoji: "💳", warn: true },
];
const BUYER_PICKUP_ROWS = [
  { code: "ORD-2891", seller: "GlowCo Cosmetics", total: "$320.00",   waiting: "ready since 4h",  warehouse: "Brooklyn, NY" },
  { code: "ORD-2710", seller: "Sunshine Beauty",  total: "$1,090.00", waiting: "ready since 2d",  warehouse: "Trenton, NJ", warn: true },
  { code: "ORD-2655", seller: "FreshLine Co.",    total: "$760.00",   waiting: "ready since 5h",  warehouse: "Newark, NJ" },
  { code: "ORD-2410", seller: "GlowCo Cosmetics", total: "$240.00",   waiting: "ready since 6h",  warehouse: "Brooklyn, NY" },
];
const BUYER_PROGRESS_ROWS = [
  { code: "ORD-3315", seller: "GlowCo Cosmetics", stage: "confirm",  chip: "amber",  emoji: "⏳", label: "Seller confirming",  total: "$420.00",  age: "30m" },
  { code: "ORD-3284", seller: "Sunshine Beauty",  stage: "pack",     chip: "blue",   emoji: "📦", label: "Seller is packing",  total: "$2,140.00",age: "1d" },
  { code: "ORD-3209", seller: "FreshLine Co.",    stage: "transit",  chip: "indigo", emoji: "🚚", label: "In transit · ETA Fri",total: "$960.00", age: "2d" },
];

// ── Pulse cards for seller dashboard ─────────────────────────────────────
const PULSE_CARDS = [
  { icon: Bell,        tone: "rose",    title: "Action backlog",        big: "14 orders", sub: "awaiting your confirm · 3 over 24h SLA", cta: "Open Action queue" },
  { icon: DollarSign,  tone: "amber",   title: "Revenue at risk",       big: "$42,310",   sub: "in awaiting-pay past due date",            cta: "Chase 6 invoices" },
  { icon: Box,         tone: "violet",  title: "Warehouse jam",         big: "23 orders", sub: "ready · buyer hasn't booked pickup",        cta: "Nudge buyers" },
  { icon: Truck,       tone: "indigo",  title: "Today's pickups",       big: "8",         sub: "carrier hand-offs scheduled",               cta: "Print labels" },
  { icon: TrendingUp,  tone: "emerald", title: "DSO trend",             big: "31d",       sub: "↑ 4d this week",                            cta: "View cashflow" },
  { icon: Users,       tone: "slate",   title: "New buyers",            big: "5",         sub: "first orders received this week",           cta: "Send welcome" },
];

// ─────────────────────────────────────────────────────────────────────────
export default function OrdersTabsRedesignPreview() {
  const [portal, setPortal] = useState("buyer");
  const [mode,   setMode]   = useState("after");
  const tabs = portal === "buyer"
    ? (mode === "after" ? BUYER_AFTER : BUYER_BEFORE)
    : (mode === "after" ? SELLER_AFTER : SELLER_BEFORE);

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Sticky preview header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1280px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal mockup · v1</div>
            <div className="display text-lg mt-0.5">Orders tab consolidation · 8 → 5/6 tabs</div>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Portal:</span>
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {PORTALS.map(p => (
                <button key={p.key}
                  onClick={() => setPortal(p.key)}
                  data-testid={`tab-preview-portal-${p.key}`}
                  className={`rounded-full px-4 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${portal === p.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">View:</span>
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {MODES.map(m => (
                <button key={m.key}
                  onClick={() => setMode(m.key)}
                  data-testid={`tab-preview-mode-${m.key}`}
                  className={`rounded-full px-4 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${mode === m.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Headline summary */}
        <div className="max-w-[1280px] mx-auto px-8 pb-3 text-[12px] text-foreground/65">
          {portal === "buyer" ? (
            mode === "after" ? (
              <>Buyer · <strong className="text-foreground">7 tabs</strong> (was 8) · adds the missing <strong className="text-foreground">Schedule pickup</strong> action that was hidden in the old "Preparing" bucket. <span className="text-rose-700">Two action tabs separated</span> for clarity. "All" stays as a search/filter fallback.</>
            ) : (
              <>Buyer · <strong className="text-foreground">8 tabs today</strong> · note: orders in <span className="mono">ready_for_pickup</span> status currently misfile under "Confirming" because the stage logic has no branch for it — that's the <strong className="text-rose-700">real bug</strong> this fix closes.</>
            )
          ) : (
            mode === "after" ? (
              <>Seller · <strong className="text-foreground">5 tabs</strong> (was 8) · "All" stays leftmost as the orientation anchor. Confirming + Preparing collapsed into <strong className="text-foreground">Action needed</strong>. Awaiting-pay, Ready, In-transit collapsed into <strong className="text-foreground">In progress</strong> with sub-state chips.</>
            ) : (
              <>Seller · <strong className="text-foreground">8 tabs today</strong> · 6 of them are passive monitoring views. The 2 real action tabs (Confirming + Preparing) drown in the row.</>
            )
          )}
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-8 py-10">
        {/* Tab bar mockup */}
        <SectionHeader n={1} title={`${portal === "buyer" ? "Buyer" : "Seller"} Orders · top tab bar (${mode === "after" ? "proposed" : "today"})`} />
        <div className="rounded-2xl border border-border bg-white p-4 overflow-x-auto">
          <div className="flex items-center gap-2 flex-wrap">
            {tabs.map((t, i) => {
              const activeIdx =
                mode === "after"
                  ? portal === "buyer" ? 3 : 1     // Schedule pickup / Action needed
                  : portal === "buyer" ? 3 : 1;   // To pay / Confirming
              return (
                <TabPill key={t.key} tab={t} active={i === activeIdx} />
              );
            })}
          </div>
        </div>

        {/* Sidebar mirror */}
        <SectionHeader n={2} className="mt-10" title={`${portal === "buyer" ? "Buyer" : "Seller"} sidebar · mirrors the top tabs (single source of truth)`} />
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          <SidebarMock tabs={tabs} portal={portal} />
          <div className="rounded-2xl border border-dashed border-border bg-white p-5 text-[12px] text-foreground/65 leading-relaxed">
            <strong className="text-foreground">Why mirror?</strong>
            {" "}One canonical taxonomy. The pill bar at the top is for fast scanning during heavy use; the sidebar is the muscle-memory entry point when navigating from another section. Today they drift — sidebar shows raw stage names ("Awaiting payment", "Ready for pickup") while the top bar uses friendlier labels. After this change both speak the same language.
            <br/><br/>
            <strong className="text-foreground">Counts shown in sidebar</strong> match the top-bar pill counts exactly — single source of truth pulling from the same /orders aggregation endpoint.
            {portal === "buyer" && mode === "after" && (
              <>
                <br/><br/>
                <strong className="text-foreground">Bonus:</strong> the "Schedule pickup" sidebar entry surfaces an action that's currently invisible to buyers — they often forget the warehouse is waiting.
              </>
            )}
            {portal === "seller" && mode === "after" && (
              <>
                <br/><br/>
                <strong className="text-foreground">Bonus:</strong> "Action needed" with a count badge becomes the first thing a seller sees in the morning — it's their daily worklist.
              </>
            )}
          </div>
        </div>

        {/* Live preview of the active tab content */}
        <SectionHeader n={3} className="mt-10" title="Inside the highlighted tab · how rows differentiate sub-state" />
        {mode === "after" && portal === "seller" && <SellerActionContent />}
        {mode === "after" && portal === "seller" && <SellerProgressContent />}
        {mode === "after" && portal === "buyer"  && <BuyerPayContent />}
        {mode === "after" && portal === "buyer"  && <BuyerPickupContent />}
        {mode === "after" && portal === "buyer"  && <BuyerProgressContent />}
        {mode === "before" && (
          <div className="rounded-2xl border border-dashed border-border bg-white p-8 text-center text-[13px] text-foreground/55">
            Today's view shows one of the {portal === "buyer" ? "6" : "6"} passive tabs at a time — same row layout you already use.
            <br/>
            <span className="mt-2 inline-block text-[12px] text-foreground/45">Switch the toggle to <strong>Proposed</strong> to see the merged tabs in action.</span>
          </div>
        )}

        {/* Seller dashboard pulse cards */}
        {portal === "seller" && mode === "after" && (
          <>
            <SectionHeader n={4} className="mt-14" title="Seller Dashboard · new “Pulse” cards (replaces the buyer-style Journey)" />
            <div className="grid md:grid-cols-3 gap-4">
              {PULSE_CARDS.map(c => (
                <PulseCard key={c.title} {...c} />
              ))}
            </div>
            <div className="mt-3 text-[11px] text-foreground/50">
              Why not a Journey tab for sellers? Volume mismatch — buyers track 5–50 active orders, sellers track 200–2,000. Per-order narrative becomes noise. Sellers want aggregate health: SLA breaches, revenue at risk, warehouse throughput.
            </div>
          </>
        )}

        {portal === "buyer" && mode === "after" && (
          <>
            <SectionHeader n={4} className="mt-14" title="Why this design wins for buyers" />
            <div className="grid md:grid-cols-3 gap-4">
              <Pillar
                icon={<Zap className="w-4 h-4" />}
                title="Two action tabs · clearly separated"
                body="Pay = financial. Schedule pickup = logistics. Both are on you, but they feel different. Splitting them means the badge counts always tell you exactly what's overdue and where."
              />
              <Pillar
                icon={<Activity className="w-4 h-4" />}
                title="One “In progress” tab"
                body="Confirming + Preparing + Shipping are all 'someone else is doing things' from the buyer's POV. Sub-chips on each row tell you exactly which stage. Less scrolling, same data depth."
              />
              <Pillar
                icon={<Sparkles className="w-4 h-4" />}
                title="Journey stays the default"
                body="The visual story view remains your landing page. It's emotional reassurance — you spent money, you want to see your PO physically moving."
              />
            </div>
          </>
        )}

        {/* Migration / risk note */}
        <div className="mt-14 rounded-2xl border border-dashed border-border bg-white p-5 text-[12px] text-foreground/65 leading-relaxed">
          <strong className="text-foreground">Migration notes:</strong>
          {" "}URL hashes (e.g. <span className="mono">/buyer/orders#pack</span>) keep working — old links 301 to the new merged tabs. Existing pytests pinned to <span className="mono">data-testid="seller-tab-preparing"</span> get a small backwards-compat shim. The Adjust button we just shipped on the Preparing tab moves with the orders into the new "Action needed" tab — no functional regression.
          {portal === "buyer" && mode === "after" && (
            <>
              <br/><br/>
              <strong className="text-foreground">Bonus fix:</strong>
              {" "}This redesign closes the <span className="mono">ready_for_pickup</span> misclassification bug — orders in that state currently show as "Seller Confirms" in the buyer's Journey because the stage logic has no branch for ready-but-not-shipped.
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar mock ─────────────────────────────────────────────────────── */
function SidebarMock({ tabs, portal }) {
  const portalLabel = portal === "buyer" ? "Buyer portal" : "Seller portal";
  const accent = portal === "buyer" ? "bg-violet-500" : "bg-emerald-500";
  return (
    <div className="rounded-2xl border border-border bg-foreground text-background overflow-hidden">
      {/* Sidebar brand strip */}
      <div className="px-4 py-4 border-b border-white/10 flex items-center gap-2">
        <div className={`w-7 h-7 rounded-md ${accent} flex items-center justify-center`}>
          <span className="text-foreground text-[13px] mono font-bold">T</span>
        </div>
        <div className="leading-tight">
          <div className="text-[13px] font-medium">TRAQO</div>
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-background/55">{portalLabel}</div>
        </div>
      </div>

      {/* Section heading */}
      <div className="px-4 pt-4 pb-2 text-[10px] mono uppercase tracking-[0.18em] text-background/40">
        Core
      </div>

      {/* Mock sibling entries above Orders */}
      <div className="px-2 space-y-0.5">
        <div className="px-3 py-2 rounded-md text-[12.5px] text-background/55 hover:bg-white/5">Dashboard</div>
        <div className="px-3 py-2 rounded-md text-[12.5px] text-background/55 hover:bg-white/5">Buyers</div>
      </div>

      {/* Orders accordion (expanded) */}
      <div className="mt-1">
        <div className="px-5 py-2 flex items-center justify-between text-[12.5px] bg-white/5">
          <div className="flex items-center gap-2">
            <Box className="w-3.5 h-3.5"/>
            <span className="font-medium">Orders</span>
          </div>
          <ChevronRight className="w-3.5 h-3.5 -rotate-90" />
        </div>
        {/* Sub-items mirror the top tabs */}
        <div className="px-2 py-1 space-y-0.5">
          {tabs.map((t) => {
            const tone = {
              amber:   "bg-amber-500/15 border-amber-400/30 text-amber-200",
              rose:    "bg-rose-500/15 border-rose-400/30 text-rose-200",
              blue:    "bg-blue-500/15 border-blue-400/30 text-blue-200",
              indigo:  "bg-indigo-500/15 border-indigo-400/30 text-indigo-200",
              teal:    "bg-teal-500/15 border-teal-400/30 text-teal-200",
              emerald: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200",
            }[t.tone] || "bg-white/10 border-white/10 text-background/65";
            const Icon = t.icon;
            return (
              <div key={t.key} className="pl-7 pr-3 py-1.5 rounded-md flex items-center gap-2 hover:bg-white/5 cursor-default">
                <Icon className="w-3.5 h-3.5 text-background/65 shrink-0" />
                <span className="text-[12.5px] text-background/85 flex-1 truncate">{t.label}</span>
                {t.action && (
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400" title="Action needed" />
                )}
                {t.isNew && (
                  <span className="text-[8.5px] mono uppercase tracking-[0.14em] text-emerald-200 bg-emerald-500/20 border border-emerald-400/30 rounded px-1 leading-[12px]">
                    new
                  </span>
                )}
                {t.count != null && (
                  <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full border ${tone}`}>
                    {t.count}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mock siblings below Orders */}
      <div className="px-2 mt-2 space-y-0.5 pb-4">
        <div className="px-3 py-2 rounded-md text-[12.5px] text-background/55 hover:bg-white/5">Payouts</div>
        <div className="px-3 py-2 rounded-md text-[12.5px] text-background/55 hover:bg-white/5">Insurance</div>
      </div>
    </div>
  );
}

/* ─── Tab pill ─────────────────────────────────────────────────────────── */
function TabPill({ tab, active }) {
  const tone = tab.tone || "slate";
  const ring = {
    amber:   "ring-amber-300 bg-amber-50 text-amber-900",
    rose:    "ring-rose-300 bg-rose-50 text-rose-900",
    blue:    "ring-blue-300 bg-blue-50 text-blue-900",
    indigo:  "ring-indigo-300 bg-indigo-50 text-indigo-900",
    teal:    "ring-teal-300 bg-teal-50 text-teal-900",
    emerald: "ring-emerald-300 bg-emerald-50 text-emerald-900",
    slate:   "ring-slate-300 bg-slate-50 text-slate-900",
  }[tone] || "ring-slate-300 bg-slate-50 text-slate-900";
  const isAction = tab.action;
  return (
    <div className={`relative inline-flex items-center gap-2 rounded-full px-4 h-10 text-[12.5px] transition-all ${active ? "ring-2 ring-foreground bg-foreground text-background" : `ring-1 ${ring}`}`}>
      <tab.icon className="w-3.5 h-3.5"/>
      <span className="font-medium">{tab.label}</span>
      {tab.count != null && (
        <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-background/15" : "bg-white/70 border border-current/20"}`}>
          {tab.count}
        </span>
      )}
      {isAction && !active && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white" title="Needs your action" />
      )}
      {tab.isNew && !active && (
        <span className="text-[9px] mono uppercase tracking-[0.14em] text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1 leading-[14px]">
          new
        </span>
      )}
    </div>
  );
}

/* ─── Seller content ───────────────────────────────────────────────────── */
function SellerActionContent() {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-border bg-amber-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px]">
          <Zap className="w-3.5 h-3.5 text-amber-700" />
          <span className="font-medium text-amber-900">Action needed · 55 orders</span>
        </div>
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-amber-700/70">
          Sub-filter: <strong>All actions</strong> · Confirming · Preparing
        </div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
          <tr>
            <th className="text-left p-3 font-normal">Order</th>
            <th className="text-left p-3 font-normal">Buyer</th>
            <th className="text-left p-3 font-normal">What to do</th>
            <th className="text-right p-3 font-normal">Total</th>
            <th className="text-right p-3 font-normal">Age</th>
          </tr>
        </thead>
        <tbody>
          {SELLER_ACTION_ROWS.map(r => (
            <tr key={r.code} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 mono">{r.code}</td>
              <td className="p-3">{r.buyer}</td>
              <td className="p-3">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] ${
                  r.chip === "amber" ? "bg-amber-100 text-amber-900" :
                  r.chip === "blue" ? "bg-blue-100 text-blue-900" : "bg-slate-100"
                }`}>
                  <span>{r.emoji}</span>
                  {r.label}
                </span>
                {r.stage === "preparing" && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-white border border-border px-2 h-6 text-[10.5px]">
                    <Pencil className="w-3 h-3"/>Adjust
                  </span>
                )}
              </td>
              <td className="p-3 text-right mono">{r.total}</td>
              <td className="p-3 text-right text-foreground/55 text-[12px]">
                {r.age}
                {r.sla && <span className="ml-1 text-rose-600 mono">SLA</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SellerProgressContent() {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-blue-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px]">
          <Activity className="w-3.5 h-3.5 text-blue-700" />
          <span className="font-medium text-blue-900">In progress · 101 orders</span>
        </div>
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-blue-700/70 flex items-center gap-2 flex-wrap">
          Sub-filter:
          <Chip tone="rose">Awaiting pay · 22</Chip>
          <Chip tone="teal">Buyer to book · 23</Chip>
          <Chip tone="indigo">In transit · 56</Chip>
        </div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
          <tr>
            <th className="text-left p-3 font-normal">Order</th>
            <th className="text-left p-3 font-normal">Buyer</th>
            <th className="text-left p-3 font-normal">Sub-state</th>
            <th className="text-right p-3 font-normal">Total</th>
            <th className="text-right p-3 font-normal">Age</th>
          </tr>
        </thead>
        <tbody>
          {SELLER_PROGRESS_ROWS.map(r => (
            <tr key={r.code} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 mono">{r.code}</td>
              <td className="p-3">{r.buyer}</td>
              <td className="p-3">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] ${
                  r.chip === "rose" ? "bg-rose-100 text-rose-900" :
                  r.chip === "teal" ? "bg-teal-100 text-teal-900" :
                  r.chip === "indigo" ? "bg-indigo-100 text-indigo-900" : "bg-slate-100"
                }`}>
                  <span>{r.emoji}</span>{r.label}
                </span>
                {r.warn && <span className="ml-2 text-[10px] mono uppercase tracking-[0.12em] text-rose-700">{r.warn}</span>}
              </td>
              <td className="p-3 text-right mono">{r.total}</td>
              <td className="p-3 text-right text-foreground/55 text-[12px]">{r.age}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Buyer content ────────────────────────────────────────────────────── */
function BuyerPayContent() {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-border bg-rose-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px]">
          <CreditCard className="w-3.5 h-3.5 text-rose-700" />
          <span className="font-medium text-rose-900">To pay · 2 invoices</span>
        </div>
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-rose-700/70">
          1 due today · $1,260 owed
        </div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
          <tr>
            <th className="text-left p-3 font-normal">Order</th>
            <th className="text-left p-3 font-normal">Seller</th>
            <th className="text-left p-3 font-normal">Invoice</th>
            <th className="text-right p-3 font-normal">Total</th>
            <th className="text-right p-3 font-normal">Due</th>
            <th className="text-right p-3 font-normal">Action</th>
          </tr>
        </thead>
        <tbody>
          {BUYER_PAY_ROWS.map(r => (
            <tr key={r.code} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 mono">{r.code}</td>
              <td className="p-3">{r.seller}</td>
              <td className="p-3 mono text-[12px]">{r.invoice}</td>
              <td className="p-3 text-right mono">{r.total}</td>
              <td className="p-3 text-right text-[12px]">
                <span className={r.warn ? "text-rose-700 font-medium" : "text-foreground/65"}>{r.due}</span>
              </td>
              <td className="p-3 text-right">
                <span className="inline-flex items-center gap-1 rounded-full bg-foreground text-background px-3 h-7 text-[11px] mono uppercase tracking-[0.12em]">
                  Pay now
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BuyerPickupContent() {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-border bg-amber-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px]">
          <Truck className="w-3.5 h-3.5 text-amber-700" />
          <span className="font-medium text-amber-900">Schedule pickup · 4 orders</span>
          <span className="ml-2 text-[10px] mono uppercase tracking-[0.14em] text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1 leading-[14px]">new</span>
        </div>
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-amber-700/70">
          1 sitting &gt; 24h · seller's warehouse waiting
        </div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
          <tr>
            <th className="text-left p-3 font-normal">Order</th>
            <th className="text-left p-3 font-normal">Seller</th>
            <th className="text-left p-3 font-normal">Pickup from</th>
            <th className="text-left p-3 font-normal">Waiting</th>
            <th className="text-right p-3 font-normal">Total</th>
            <th className="text-right p-3 font-normal">Action</th>
          </tr>
        </thead>
        <tbody>
          {BUYER_PICKUP_ROWS.map(r => (
            <tr key={r.code} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 mono">{r.code}</td>
              <td className="p-3">{r.seller}</td>
              <td className="p-3 text-[12px] text-foreground/65">{r.warehouse}</td>
              <td className="p-3 text-[12px]">
                <span className={r.warn ? "text-rose-700 font-medium" : "text-foreground/65"}>{r.waiting}</span>
              </td>
              <td className="p-3 text-right mono">{r.total}</td>
              <td className="p-3 text-right">
                <span className="inline-flex items-center gap-1 rounded-full bg-foreground text-background px-3 h-7 text-[11px] mono uppercase tracking-[0.12em]">
                  Book carrier
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BuyerProgressContent() {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-blue-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px]">
          <Activity className="w-3.5 h-3.5 text-blue-700" />
          <span className="font-medium text-blue-900">In progress · 19 orders</span>
        </div>
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-blue-700/70 flex items-center gap-2 flex-wrap">
          Sub-filter:
          <Chip tone="amber">Confirming · 3</Chip>
          <Chip tone="blue">Preparing · 8</Chip>
          <Chip tone="indigo">Shipping · 12</Chip>
        </div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
          <tr>
            <th className="text-left p-3 font-normal">Order</th>
            <th className="text-left p-3 font-normal">Seller</th>
            <th className="text-left p-3 font-normal">Sub-state</th>
            <th className="text-right p-3 font-normal">Total</th>
            <th className="text-right p-3 font-normal">Age</th>
          </tr>
        </thead>
        <tbody>
          {BUYER_PROGRESS_ROWS.map(r => (
            <tr key={r.code} className="border-t border-border hover:bg-secondary/20">
              <td className="p-3 mono">{r.code}</td>
              <td className="p-3">{r.seller}</td>
              <td className="p-3">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 h-6 text-[11px] ${
                  r.chip === "amber" ? "bg-amber-100 text-amber-900" :
                  r.chip === "blue" ? "bg-blue-100 text-blue-900" :
                  r.chip === "indigo" ? "bg-indigo-100 text-indigo-900" : "bg-slate-100"
                }`}>
                  <span>{r.emoji}</span>{r.label}
                </span>
              </td>
              <td className="p-3 text-right mono">{r.total}</td>
              <td className="p-3 text-right text-foreground/55 text-[12px]">{r.age}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Pulse cards ──────────────────────────────────────────────────────── */
function PulseCard({ icon: Icon, tone, title, big, sub, cta }) {
  const tones = {
    rose:    "border-rose-200 bg-rose-50/60",
    amber:   "border-amber-200 bg-amber-50/60",
    violet:  "border-violet-200 bg-violet-50/60",
    indigo:  "border-indigo-200 bg-indigo-50/60",
    emerald: "border-emerald-200 bg-emerald-50/60",
    slate:   "border-slate-200 bg-slate-50/60",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone] || tones.slate}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-border flex items-center justify-center">
          <Icon className="w-4 h-4 text-foreground/70" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/55">{title}</div>
          <div className="display text-2xl mt-1">{big}</div>
          <div className="text-[12px] text-foreground/60 mt-0.5 leading-relaxed">{sub}</div>
          <button className="mt-3 text-[11px] mono uppercase tracking-[0.14em] text-foreground/70 hover:text-foreground inline-flex items-center gap-1">
            {cta} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Primitives ───────────────────────────────────────────────────────── */
function SectionHeader({ n, title, className = "" }) {
  return (
    <div className={`flex items-center gap-3 mb-4 ${className}`}>
      <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-[11px] mono">{n}</div>
      <div className="text-[13px] text-foreground/75">{title}</div>
    </div>
  );
}

function Chip({ tone = "slate", children }) {
  const tones = {
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    amber:   "bg-amber-100 text-amber-900 border-amber-200",
    rose:    "bg-rose-100 text-rose-900 border-rose-200",
    blue:    "bg-blue-100 text-blue-900 border-blue-200",
    indigo:  "bg-indigo-100 text-indigo-900 border-indigo-200",
    teal:    "bg-teal-100 text-teal-900 border-teal-200",
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 h-5 text-[10.5px] mono uppercase tracking-[0.10em] ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="w-9 h-9 rounded-full bg-secondary/60 border border-border flex items-center justify-center text-foreground/70">{icon}</div>
      <div className="mt-3 text-[13px] font-medium">{title}</div>
      <div className="mt-1 text-[12px] text-foreground/65 leading-relaxed">{body}</div>
    </div>
  );
}
